import { DataUtilities } from "../Common/DataUtilities";
import { IConfigStorage } from "../Common/IConfigStorage";
import { ExpressionConfiguration } from "../Expressions/ExpressionConfiguration";
import { ExpressionValueType } from "../Expressions/ExpressionValueType";
import { DataDefinition } from "../Common/Data/DataDefinition";
import { IDesignValidationProvider, ValidationIssue } from "../Common/IDesignValidationProvider";
import { Executable } from "../Common/Execution/Executable";
import { ExecutionData } from "../Common/Data/ExecutionData/ExecutionData";
import { ITestingContext } from "../Common/Testing/ITestingContext";
import { IDataDefinition } from "../Common/Data/Definitions/IDataDefinition";
import { StringDataDefinition } from "../Common/Data/Definitions/StringDataDefinition";

export class MappingRule implements IConfigStorage {
    id: string;
    targetPath: string = "";
    mapping: ExpressionConfiguration;

    constructor(targetPath: string) {
        this.targetPath = targetPath;

        this.id = DataUtilities.generateUniqueId();
        this.mapping = new ExpressionConfiguration();
        this.mapping.allowRootUnset = true;
    }

    get empty(): boolean {
        return (
            this.mapping.valueType === ExpressionValueType.UNSET ||
            (this.mapping.valueType === ExpressionValueType.VALUE && !this.mapping.value) ||
            (this.mapping.valueType === ExpressionValueType.VARIABLE && !this.mapping.variableName) ||
            (this.mapping.valueType === ExpressionValueType.SUBEXPRESSION && !this.mapping.expression)
        );
    }

    getInboundPaths(): string[] {
        return this.mapping.getAllVariables();
    }

    getStorageData(): object {
        let obj = {};
        Reflect.set(obj, "id", this.id);
        Reflect.set(obj, "targetPath", this.targetPath);
        Reflect.set(obj, "mapping", this.mapping.getStorageData());

        return obj;
    }

    getValidationIssue(odatadef: DataDefinition): ValidationIssue[] {
        let issues: ValidationIssue[] = [];

        if (this.targetPath === "") {
            issues.push(new ValidationIssue("target path is required."));
        } else {
            try {
                DataUtilities.getFieldFromDataDefinitionByName(odatadef.fields, this.targetPath);
            } catch (e) {
                issues.push(new ValidationIssue(`[${this.targetPath}]: invalid target path`));
            }
        }

        issues.push(
            ...this.mapping.getDesignValidationIssues().map((issue) => {
                issue.issue = `[${this.targetPath}]: ${issue.issue}`;

                return issue;
            })
        );

        return issues;
    }

    setStorageData(data: object) {
        this.id = Reflect.get(data, "id");
        this.targetPath = Reflect.get(data, "targetPath");
        this.mapping.setStorageData(Reflect.get(data, "mapping"));
    }
}

export class MappingConfiguration extends Executable implements IDesignValidationProvider {
    title?: string;

    private _mappingRules: MappingRule[] = [];

    get mappingRules() {
        let resultData = this.getExecutionResult();

        for (let r of this._mappingRules) {
            let dd = DataUtilities.getFieldFromDataDefinitionByName(resultData.fields, r.targetPath);

            if (dd && dd !== null) {
                r.mapping.executionResult = () => {
                    // we should not have to use default
                    return dd || new StringDataDefinition();
                };
            }

            r.mapping.inputData = () => {
                return this.getInputDefinition();
            };
        }

        return this._mappingRules;
    }

    get executionResult() {
        return this._executionResult;
    }

    set executionResult(res: (() => DataDefinition) | DataDefinition | undefined) {
        this._executionResult = res;

        let odef = this.getExecutionResult();

        let fields = DataUtilities.getAllSubFieldsWithPath(odef);
        for (let { path, field } of fields) {
            if (!this.getMappingRuleByTargetPath(path)) this.addMappingRule(path, field);
        }
    }

    // eslint-disable-next-line
    run(data: ExecutionData, testContext: ITestingContext | undefined) {
        throw new Error("not implemented");
    }

    addMappingRule(targetPath: string, field?: IDataDefinition): MappingRule {
        if (!field) {
            field = DataUtilities.getFieldFromDataDefinitionByName(
                this.getExecutionResult().fields,
                targetPath
            ) as IDataDefinition;
        }

        let maprule = new MappingRule(targetPath);

        maprule.mapping.inputData = () => this.getInputDefinition();

        if (!field) {
            throw new Error(`[${targetPath}] is invalid.`);
        }

        maprule.mapping.executionResult = () => field!;

        this._mappingRules.push(maprule);

        return maprule;
    }

    removeMappingRule(id: string) {
        let index = this._mappingRules.findIndex((m) => m.id === id);

        if (index >= 0) {
            this._mappingRules.splice(index, 1);
        }
    }

    getInvalidRules(): MappingRule[] {
        let result: MappingRule[] = [];

        for (let mappingRule of this.mappingRules) {
            if (mappingRule.targetPath === "") {
                if (mappingRule.mapping.valueType === ExpressionValueType.UNSET) {
                    this.removeMappingRule(mappingRule.id);
                } else {
                    result.push(mappingRule);
                }
            } else {
                try {
                    DataUtilities.getFieldFromDataDefinitionByName(
                        this.getExecutionResult().fields,
                        mappingRule.targetPath
                    );
                } catch (e) {
                    if (mappingRule.mapping.valueType === ExpressionValueType.UNSET) {
                        this.removeMappingRule(mappingRule.id);
                    } else {
                        result.push(mappingRule);
                    }
                }
            }
        }
        return result;
    }

    hookDirectConnect(sourcePath: string, targetPath: string): MappingRule | undefined {
        // todo: replace if exists

        let idatadef = this.getInputDefinition();

        // not sure if it needs
        DataUtilities.getFieldFromDataDefinitionByName(idatadef.fields, sourcePath);

        let odatadef = this.getExecutionResult();
        DataUtilities.getFieldFromDataDefinitionByName(odatadef.fields, targetPath);

        let mappingRule = this.getMappingRuleByTargetPath(targetPath);

        if (!mappingRule) return undefined;

        mappingRule.mapping.variableName = sourcePath;

        return mappingRule;
    }

    getStorageData(): object {
        let obj = super.getStorageData();
        Reflect.set(
            obj,
            "mappingRules",
            this.mappingRules.map((m) => m.getStorageData())
        );

        return obj;
    }

    setStorageData(data: object) {
        super.setStorageData(data);

        let mappingRules = Reflect.get(data, "mappingRules") as object[];

        this._mappingRules = mappingRules.map((m) => {
            let mappingRule = new MappingRule("");
            mappingRule.setStorageData(m);
            return mappingRule;
        });
    }

    getMappingRuleByTargetPath(targetPath: string): MappingRule {
        for (let mappingRule of this.mappingRules) {
            if (mappingRule.targetPath === targetPath) {
                return mappingRule;
            }
        }

        return this.addMappingRule(targetPath);
    }

    find(id: string): MappingRule | undefined {
        return this.mappingRules.find((m) => m.id === id);
    }

    getMappingRulesByInputPath(path: string): MappingRule[] {
        let rules: MappingRule[] = [];

        for (let mappingRule of this._mappingRules) {
            if (mappingRule.mapping.getAllVariables().includes(path)) {
                rules.push(mappingRule);
            }
        }

        return rules;
    }

    getDesignValidationIssues(): ValidationIssue[] {
        let issues: ValidationIssue[] = [];

        for (let mappingrule of this._mappingRules) {
            issues.push(...mappingrule.getValidationIssue(this.getExecutionResult()));
        }

        if (this.getInputDefinition().fields.length === 0) {
            issues.push(new ValidationIssue("input datadef is not configured"));
        }

        if (this.getExecutionResult().fields.length === 0) {
            issues.push(new ValidationIssue("output datadef is not configured"));
        }

        return issues;
    }
}
